import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Switch,
  Table,
  Tabs,
  Tooltip,
} from 'antd';
import toast from 'react-hot-toast';
import { Ban, Bell, Check, List, Mail, ShieldAlert, TriangleAlert } from 'lucide-react';
import {
  AdminButton,
  AdminCard,
  AdminPageHeader,
  AdminStatCard,
  AdminStatusPill,
} from '../../ui';
import type { AdminTone } from '../../ui';
import {
  AccessBlock,
  AlertPreview,
  BlockType,
  LibraryItem,
  GuardStats,
  QueryLog,
  RulePayload,
  SecurityRule,
  addBlock,
  addFromLibrary,
  createRule,
  deleteRule,
  getBlocks,
  getLibrary,
  getAlertPreview,
  getGuardStats,
  getQueryLogs,
  getRules,
  markReviewed,
  removeBlock,
  sendAlertTest,
  testRules,
  updateRule,
} from '../../../../api/admin/query-guard.api';

const SEVERITY_COLOR: Record<string, string> = { HIGH: 'red', MEDIUM: 'orange', LOW: 'blue' };
const SEVERITY_LABEL: Record<string, string> = { HIGH: 'Cao', MEDIUM: 'Trung bình', LOW: 'Thấp' };
const ACTION_LABEL: Record<string, string> = {
  LOG: 'Chỉ ghi',
  WARN: 'Cảnh báo',
  BLOCK: 'Chặn',
};
const ACTION_COLOR: Record<string, string> = { LOG: 'default', WARN: 'orange', BLOCK: 'red' };
const TYPE_LABEL: Record<string, string> = { KEYWORD: 'Nội dung xấu', ATTACK: 'Mẫu tấn công' };
const TAKEN_LABEL: Record<string, string> = {
  LOGGED: 'Đã ghi',
  WARNED: 'Cảnh báo',
  BLOCKED: 'Đã chặn',
};

const fmtTime = (s: string) => {
  try {
    return new Date(s).toLocaleString('vi-VN');
  } catch {
    return s;
  }
};

const EMPTY_RULE: RulePayload = {
  name: '',
  ruleType: 'KEYWORD',
  matchType: 'CONTAINS',
  pattern: '',
  severity: 'MEDIUM',
  action: 'WARN',
  enabled: true,
  note: '',
};

// Nhãn viên thuốc thay cho antd Tag (đổi màu antd sang họ màu admin).
const TAG_TONE: Record<string, AdminTone> = {
  red: 'danger',
  orange: 'warning',
  blue: 'brand',
  purple: 'brand',
  cyan: 'brand',
  green: 'success',
};
const Tag = ({ color, children }: { color?: string; children?: React.ReactNode }) => (
  <AdminStatusPill tone={(color && TAG_TONE[color]) || 'neutral'}>{children}</AdminStatusPill>
);

const StatCard = ({
  label,
  value,
  tone,
  icon,
}: {
  label: string;
  value: number;
  tone?: AdminTone;
  icon: React.ReactNode;
}) => <AdminStatCard label={label} value={value} tone={tone ?? 'brand'} icon={icon} />;

const ManagementQueryGuard = () => {
  const [stats, setStats] = useState<GuardStats | null>(null);
  const [alert, setAlert] = useState<AlertPreview | null>(null);
  const [sendingTest, setSendingTest] = useState(false);
  // Theo dõi trực tiếp: tự tải lại nhật ký + số liệu mỗi 5s (yêu cầu "hiển thị real time user").
  const [live, setLive] = useState(true);
  const [lastAt, setLastAt] = useState<Date | null>(null);

  // ── Nhật ký ──
  const [logs, setLogs] = useState<QueryLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [flaggedOnly, setFlaggedOnly] = useState(true);
  const [severity, setSeverity] = useState<string | undefined>();
  const [ruleType, setRuleType] = useState<string | undefined>();
  const [keyword, setKeyword] = useState('');
  const [loadingLogs, setLoadingLogs] = useState(false);

  // ── Bộ luật ──
  const [rules, setRules] = useState<SecurityRule[]>([]);
  const [loadingRules, setLoadingRules] = useState(false);
  const [editing, setEditing] = useState<SecurityRule | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<RulePayload>();

  // ── Thư viện mẫu ──
  const [lib, setLib] = useState<LibraryItem[]>([]);
  const [libCats, setLibCats] = useState<string[]>([]);
  const [libCat, setLibCat] = useState<string | undefined>();
  const [loadingLib, setLoadingLib] = useState(false);

  // ── Chặn IP / tài khoản ──
  const [blocks, setBlocks] = useState<AccessBlock[]>([]);
  const [loadingBlocks, setLoadingBlocks] = useState(false);
  const [blkType, setBlkType] = useState<BlockType>('IP');
  const [blkValue, setBlkValue] = useState('');
  const [blkReason, setBlkReason] = useState('');
  const [blkHours, setBlkHours] = useState<number | undefined>(24);

  // ── Thử luật ──
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    try {
      setStats(await getGuardStats());
    } catch {
      /* thống kê lỗi thì thôi, không chặn trang */
    }
    try {
      setAlert(await getAlertPreview(1440));
    } catch {
      /* không lấy được trạng thái cảnh báo thì bỏ qua */
    }
  }, []);

  const doSendTest = async () => {
    setSendingTest(true);
    try {
      const r = await sendAlertTest(1440);
      toast.success(`Đã gửi email cảnh báo thử tới ${r.to}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Gửi thử thất bại');
    } finally {
      setSendingTest(false);
    }
  };

  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const res = await getQueryLogs({
        pageNumber: page,
        pageSize,
        flaggedOnly,
        severity,
        ruleType,
        keyword: keyword.trim() || undefined,
      });
      setLogs(res.items);
      setTotal(res.total);
      setLastAt(new Date());
    } catch {
      toast.error('Không tải được nhật ký');
    } finally {
      setLoadingLogs(false);
    }
  }, [page, pageSize, flaggedOnly, severity, ruleType, keyword]);

  const loadRules = useCallback(async () => {
    setLoadingRules(true);
    try {
      setRules(await getRules());
    } catch {
      toast.error('Không tải được bộ luật');
    } finally {
      setLoadingRules(false);
    }
  }, []);

  const loadLib = useCallback(async () => {
    setLoadingLib(true);
    try {
      const r = await getLibrary();
      setLib(r.items);
      setLibCats(r.categories);
    } catch {
      toast.error('Không tải được thư viện mẫu');
    } finally {
      setLoadingLib(false);
    }
  }, []);

  const loadBlocks = useCallback(async () => {
    setLoadingBlocks(true);
    try {
      setBlocks(await getBlocks());
    } catch {
      toast.error('Không tải được danh sách chặn');
    } finally {
      setLoadingBlocks(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadRules();
    loadLib();
    loadBlocks();
  }, [loadStats, loadRules, loadLib, loadBlocks]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Chỉ tự tải lại khi đang ở TRANG 1 — nếu không, mỗi 5s lại nhảy về đầu, admin đang xem
  // trang 3 sẽ bị giật mất chỗ.
  useEffect(() => {
    if (!live || page !== 1) return undefined;
    const id = setInterval(() => {
      loadLogs();
      loadStats();
    }, 5000);
    return () => clearInterval(id);
  }, [live, page, loadLogs, loadStats]);

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.setFieldsValue(EMPTY_RULE);
  };

  const openEdit = (r: SecurityRule) => {
    setEditing(r);
    setCreating(true);
    form.setFieldsValue({
      name: r.name,
      ruleType: r.ruleType,
      matchType: r.matchType,
      pattern: r.pattern,
      severity: r.severity,
      action: r.action,
      enabled: r.enabled,
      note: r.note,
    });
  };

  const submitRule = async () => {
    try {
      const values = await form.validateFields();
      if (editing) await updateRule(editing.id, values);
      else await createRule(values);
      toast.success(editing ? 'Đã cập nhật luật' : 'Đã thêm luật');
      setCreating(false);
      setEditing(null);
      loadRules();
      loadStats();
    } catch (e: any) {
      if (e?.errorFields) return; // lỗi validate form
      toast.error(e?.response?.data?.message || 'Lưu luật thất bại');
    }
  };

  const toggleEnabled = async (r: SecurityRule, enabled: boolean) => {
    try {
      await updateRule(r.id, { ...r, enabled });
      setRules((prev) => prev.map((x) => (x.id === r.id ? { ...x, enabled } : x)));
      toast.success(enabled ? 'Đã bật luật' : 'Đã tắt luật');
      loadStats();
    } catch {
      toast.error('Đổi trạng thái thất bại');
    }
  };

  const removeRule = async (r: SecurityRule) => {
    try {
      await deleteRule(r.id);
      toast.success('Đã xoá luật');
      loadRules();
      loadStats();
    } catch {
      toast.error('Xoá luật thất bại');
    }
  };

  const doAddFromLib = async (item: LibraryItem) => {
    try {
      await addFromLibrary(item.key);
      toast.success(`Đã thêm "${item.name}" vào bộ luật (chế độ Cảnh báo)`);
      loadLib();
      loadRules();
      loadStats();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Thêm mẫu thất bại');
    }
  };

  const doAddBlock = async (
    type: BlockType,
    value: string,
    reason: string,
    hours?: number,
  ) => {
    if (!value.trim()) return toast.error('Chưa nhập giá trị cần chặn');
    try {
      await addBlock({
        blockType: type,
        value: value.trim(),
        reason: reason.trim(),
        expiresInHours: hours && hours > 0 ? hours : null,
      });
      toast.success(`Đã chặn ${type === 'IP' ? 'IP' : 'tài khoản'} ${value.trim()}`);
      setBlkValue('');
      setBlkReason('');
      loadBlocks();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Chặn thất bại');
    }
  };

  const doRemoveBlock = async (b: AccessBlock) => {
    try {
      await removeBlock(b.id);
      toast.success(`Đã bỏ chặn ${b.value}`);
      loadBlocks();
    } catch {
      toast.error('Bỏ chặn thất bại');
    }
  };

  const runTest = async () => {
    if (!testText.trim()) return;
    try {
      const res = await testRules(testText.trim());
      setTestResult(
        res.matched
          ? `Khớp ${res.rules.length} luật: ${res.rules.map((x) => x.name).join(', ')}`
          : 'Không khớp luật nào.',
      );
    } catch {
      toast.error('Thử luật thất bại');
    }
  };

  const doMarkReviewed = async () => {
    try {
      const res = await markReviewed();
      toast.success(`Đã đánh dấu ${res.updated} cảnh báo là đã xem`);
      loadStats();
      loadLogs();
    } catch {
      toast.error('Đánh dấu thất bại');
    }
  };

  const logColumns = [
    {
      title: 'Thời gian',
      dataIndex: 'createdAt',
      width: 160,
      render: (v: string) => <span className="whitespace-nowrap">{fmtTime(v)}</span>,
    },
    {
      title: 'Mức độ',
      dataIndex: 'severity',
      width: 110,
      render: (v: string, row: QueryLog) =>
        row.ruleId ? (
          <Tag color={SEVERITY_COLOR[v] ?? 'default'}>{SEVERITY_LABEL[v] ?? v}</Tag>
        ) : (
          <Tag>Tìm kiếm</Tag>
        ),
    },
    {
      title: 'Luật khớp',
      dataIndex: 'ruleName',
      width: 180,
      render: (v: string, row: QueryLog) =>
        row.ruleId ? (
          <div>
            <div className="font-medium">{v}</div>
            <div className="text-xs text-gray-500">{TYPE_LABEL[row.ruleType] ?? row.ruleType}</div>
          </div>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      title: 'Nội dung truy vấn',
      dataIndex: 'queryText',
      render: (v: string, row: QueryLog) => (
        <div className="min-w-[220px]">
          <div className="break-all">{v || <span className="text-gray-400">(trống)</span>}</div>
          <div className="text-xs text-gray-500 break-all">
            {row.method} {row.path}
          </div>
        </div>
      ),
    },
    {
      title: 'Người dùng',
      dataIndex: 'userId',
      width: 150,
      render: (v: string | null, row: QueryLog) => (
        <div className="text-xs">
          <div className="break-all">{v || <span className="text-gray-400">Khách</span>}</div>
          <div className="text-gray-500">{row.ip}</div>
        </div>
      ),
    },
    {
      title: 'Xử lý',
      dataIndex: 'actionTaken',
      width: 110,
      render: (v: string) => (
        <Tag color={v === 'BLOCKED' ? 'red' : v === 'WARNED' ? 'orange' : 'default'}>
          {TAKEN_LABEL[v] ?? v}
        </Tag>
      ),
    },
    {
      title: '',
      width: 150,
      render: (_: unknown, row: QueryLog) => (
        <div className="flex flex-col gap-1">
          {row.ip && (
            <Popconfirm
              title={`Chặn IP ${row.ip} trong 24 giờ?`}
              okText="Chặn"
              cancelText="Huỷ"
              onConfirm={() => doAddBlock('IP', row.ip, `Từ nhật ký: ${row.ruleName || 'truy vấn'}`, 24)}
            >
              <Button size="small" danger>
                Chặn IP
              </Button>
            </Popconfirm>
          )}
          {row.userId && (
            <Popconfirm
              title="Chặn tài khoản này trong 24 giờ?"
              okText="Chặn"
              cancelText="Huỷ"
              onConfirm={() =>
                doAddBlock('USER', row.userId as string, `Từ nhật ký: ${row.ruleName || 'truy vấn'}`, 24)
              }
            >
              <Button size="small" danger>
                Chặn tài khoản
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  const ruleColumns = [
    {
      title: 'Tên luật',
      dataIndex: 'name',
      render: (v: string, r: SecurityRule) => (
        <div>
          <div className="font-medium">{v}</div>
          {r.note && <div className="text-xs text-gray-500">{r.note}</div>}
        </div>
      ),
    },
    {
      title: 'Loại',
      dataIndex: 'ruleType',
      width: 130,
      render: (v: string) => <Tag color={v === 'ATTACK' ? 'purple' : 'cyan'}>{TYPE_LABEL[v] ?? v}</Tag>,
    },
    {
      title: 'Mẫu cần bắt',
      dataIndex: 'pattern',
      render: (v: string, r: SecurityRule) => (
        <Tooltip title={v}>
          <code className="text-xs break-all">
            {r.matchType === 'REGEX' ? '(regex) ' : ''}
            {v.length > 60 ? `${v.slice(0, 60)}…` : v}
          </code>
        </Tooltip>
      ),
    },
    {
      title: 'Mức độ',
      dataIndex: 'severity',
      width: 110,
      render: (v: string) => <Tag color={SEVERITY_COLOR[v] ?? 'default'}>{SEVERITY_LABEL[v] ?? v}</Tag>,
    },
    {
      title: 'Hành động',
      dataIndex: 'action',
      width: 120,
      render: (v: string) => <Tag color={ACTION_COLOR[v] ?? 'default'}>{ACTION_LABEL[v] ?? v}</Tag>,
    },
    {
      title: 'Bật',
      dataIndex: 'enabled',
      width: 80,
      render: (v: boolean, r: SecurityRule) => (
        <Switch size="small" checked={v} onChange={(c) => toggleEnabled(r, c)} />
      ),
    },
    {
      title: '',
      width: 130,
      render: (_: unknown, r: SecurityRule) => (
        <div className="flex gap-2">
          <Button size="small" onClick={() => openEdit(r)}>
            Sửa
          </Button>
          <Popconfirm title="Xoá luật này?" okText="Xoá" cancelText="Huỷ" onConfirm={() => removeRule(r)}>
            <Button size="small" danger>
              Xoá
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Rà truy vấn người dùng"
        actions={
          <AdminButton elevated icon={<Check size={18} />} onClick={doMarkReviewed}>
            Đánh dấu đã xem hết
          </AdminButton>
        }
      />

      {/* Dải real-time: 5 phút gần nhất — cho thấy hệ thống đang sống, không phải số liệu tĩnh. */}
      <div className="adm-strip flex flex-wrap items-center gap-x-8 gap-y-2">
        <span className="text-sm font-semibold">5 phút gần nhất</span>
        <span className="text-sm">
          <b className="text-lg">{stats?.activeUsers5m ?? 0}</b> người dùng đang hoạt động
        </span>
        <span className="text-sm">
          <b className="text-lg">{stats?.queries5m ?? 0}</b> truy vấn
        </span>
        <span className="text-sm">
          <b className="text-lg" style={(stats?.violations5m ?? 0) > 0 ? { color: 'var(--adm-danger)' } : undefined}>
            {stats?.violations5m ?? 0}
          </b>{' '}
          vi phạm
        </span>
        {page !== 1 && live && (
          <span className="text-xs" style={{ color: 'var(--adm-warning)' }}>
            Đang xem trang {page} — tạm ngưng tự tải lại để không bị nhảy trang
          </span>
        )}
        <span className="ml-auto flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-2 text-sm">
            <Switch size="small" checked={live} onChange={setLive} />
            <span className={live ? 'font-semibold' : 'ui-muted'} style={live ? { color: 'var(--adm-success)' } : undefined}>
              {live ? (
                <>
                  <span className="inline-block w-2 h-2 rounded-full mr-1 animate-pulse align-middle" style={{ background: 'var(--adm-success-fill)' }} />
                  Trực tiếp
                </>
              ) : (
                'Đã tạm dừng'
              )}
            </span>
          </span>
          {lastAt && (
            <span className="text-xs ui-muted">Cập nhật {lastAt.toLocaleTimeString('vi-VN')}</span>
          )}
        </span>
      </div>

      <div className="adm-stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        <StatCard
          label="Cảnh báo chưa xem"
          value={stats?.unreviewed ?? 0}
          icon={<Bell size={20} />}
          tone={stats && stats.unreviewed > 0 ? 'danger' : 'brand'}
        />
        <StatCard
          label="Mức cao chưa xem"
          value={stats?.unreviewedHigh ?? 0}
          icon={<TriangleAlert size={20} />}
          tone={stats && stats.unreviewedHigh > 0 ? 'danger' : 'brand'}
        />
        <StatCard label="Vi phạm 24h" value={stats?.last24h ?? 0} icon={<ShieldAlert size={20} />} />
        <StatCard label="Đã chặn 24h" value={stats?.blocked24h ?? 0} icon={<Ban size={20} />} tone="neutral" />
        <StatCard label="Luật đang bật" value={stats?.activeRules ?? 0} icon={<List size={20} />} tone="neutral" />
      </div>

      {/* Trạng thái "gửi thông báo về admin" — yêu cầu số 2 của sếp tổng. */}
      <div
        className={`adm-strip flex flex-wrap items-center gap-x-3 gap-y-1 ${
          alert?.emailConfigured ? 'adm-strip--success' : 'adm-strip--warning'
        }`}
        role="status"
      >
        <Mail size={20} className="adm-strip__icon" />
        {alert?.emailConfigured ? (
          <>
            <span>
              Gửi cảnh báo qua email tới <b>{alert.sendTo}</b> — mỗi <b>{alert.intervalMinutes} phút</b> gom 1 thư,
              chỉ mức <b>{alert.minSeverity}</b> trở lên.
            </span>
            <Button size="small" loading={sendingTest} onClick={doSendTest}>
              Gửi thử
            </Button>
          </>
        ) : (
          <span>
            Chưa bật gửi email cảnh báo. Đặt <code>QueryGuard:AlertEmail</code> trong cấu hình user-service để admin
            nhận thư khi có vi phạm (hiện chỉ hiện trên trang này và badge đỏ ở menu).
          </span>
        )}
      </div>

      <AdminCard>
      <Tabs
        defaultActiveKey="logs"
        items={[
          {
            key: 'logs',
            label: 'Nhật ký truy vấn',
            children: (
              <>
                <div className="flex gap-2 flex-wrap items-center mb-3">
                  <Select
                    value={flaggedOnly ? 'flagged' : 'all'}
                    style={{ width: 170 }}
                    onChange={(v) => {
                      setFlaggedOnly(v === 'flagged');
                      setPage(1);
                    }}
                    options={[
                      { value: 'flagged', label: 'Chỉ vi phạm' },
                      { value: 'all', label: 'Tất cả (kèm tìm kiếm)' },
                    ]}
                  />
                  <Select
                    allowClear
                    placeholder="Mức độ"
                    style={{ width: 140 }}
                    value={severity}
                    onChange={(v) => {
                      setSeverity(v);
                      setPage(1);
                    }}
                    options={[
                      { value: 'HIGH', label: 'Cao' },
                      { value: 'MEDIUM', label: 'Trung bình' },
                      { value: 'LOW', label: 'Thấp' },
                    ]}
                  />
                  <Select
                    allowClear
                    placeholder="Loại luật"
                    style={{ width: 160 }}
                    value={ruleType}
                    onChange={(v) => {
                      setRuleType(v);
                      setPage(1);
                    }}
                    options={[
                      { value: 'KEYWORD', label: 'Nội dung xấu' },
                      { value: 'ATTACK', label: 'Mẫu tấn công' },
                    ]}
                  />
                  <Input.Search
                    placeholder="Tìm trong nội dung truy vấn"
                    style={{ width: 260 }}
                    allowClear
                    onSearch={(v) => {
                      setKeyword(v);
                      setPage(1);
                    }}
                  />
                  <Button
                    onClick={() => {
                      loadLogs();
                      loadStats();
                    }}
                  >
                    Tải lại
                  </Button>
                </div>

                <Table
                  rowKey="id"
                  size="small"
                  loading={loadingLogs}
                  columns={logColumns as never}
                  dataSource={logs}
                  scroll={{ x: 1000 }}
                  pagination={{
                    current: page,
                    pageSize,
                    total,
                    showSizeChanger: true,
                    onChange: (p, ps) => {
                      setPage(p);
                      setPageSize(ps);
                    },
                  }}
                />
              </>
            ),
          },
          {
            key: 'library',
            label: `Thư viện mẫu (${lib.filter((x) => !x.added).length} chưa bật)`,
            children: (
              <>
                <p className="ui-muted text-sm mb-3">
                  Danh mục mẫu nguy cơ dựng sẵn — bấm <b>Thêm</b> là bật luật, không cần gõ biểu thức.
                  Mẫu thêm vào luôn ở chế độ <b>Cảnh báo</b>.
                </p>
                <div className="flex gap-2 flex-wrap items-center mb-3">
                  <Select
                    allowClear
                    placeholder="Tất cả nhóm"
                    style={{ width: 230 }}
                    value={libCat}
                    onChange={setLibCat}
                    options={libCats.map((c) => ({ value: c, label: c }))}
                  />
                  <Button onClick={loadLib}>Tải lại</Button>
                </div>
                <Table
                  rowKey="key"
                  size="small"
                  loading={loadingLib}
                  dataSource={libCat ? lib.filter((x) => x.category === libCat) : lib}
                  scroll={{ x: 900 }}
                  pagination={false}
                  columns={
                    [
                      {
                        title: 'Tên mẫu',
                        dataIndex: 'name',
                        render: (v: string, r: LibraryItem) => (
                          <div>
                            <div className="font-medium">{v}</div>
                            {r.note && <div className="text-xs text-gray-500">{r.note}</div>}
                          </div>
                        ),
                      },
                      {
                        title: 'Nhóm',
                        dataIndex: 'category',
                        width: 170,
                        render: (v: string) => <Tag>{v}</Tag>,
                      },
                      {
                        title: 'Mức độ',
                        dataIndex: 'severity',
                        width: 110,
                        render: (v: string) => (
                          <Tag color={SEVERITY_COLOR[v] ?? 'default'}>{SEVERITY_LABEL[v] ?? v}</Tag>
                        ),
                      },
                      {
                        title: 'Mẫu',
                        dataIndex: 'pattern',
                        render: (v: string) => (
                          <Tooltip title={v}>
                            <code className="text-xs break-all">
                              {v.length > 50 ? `${v.slice(0, 50)}…` : v}
                            </code>
                          </Tooltip>
                        ),
                      },
                      {
                        title: '',
                        width: 110,
                        render: (_: unknown, r: LibraryItem) =>
                          r.added ? (
                            <Tag color="green">Đã bật</Tag>
                          ) : (
                            <Button size="small" type="primary" onClick={() => doAddFromLib(r)}>
                              Thêm
                            </Button>
                          ),
                      },
                    ] as never
                  }
                />
              </>
            ),
          },
          {
            key: 'blocks',
            label: `Chặn IP / tài khoản (${blocks.length})`,
            children: (
              <>
                <div className="adm-strip adm-strip--warning mb-3 text-sm">
                  Chặn <b>IP</b> (chấp nhận dải kiểu <code>1.2.3.*</code>) hoặc <b>tài khoản</b>. Nên đặt{' '}
                  <b>hạn tự hết</b> thay vì chặn vĩnh viễn, tránh chặn oan người dùng thật dùng chung mạng.
                  <br />
                  Không chặn được theo <b>thiết bị</b>: mã thiết bị do máy người dùng gửi lên nên sửa được, chặn
                  theo nó chỉ là an toàn giả.
                </div>

                <div className="flex gap-2 flex-wrap items-end mb-4">
                  <div>
                    <div className="text-xs ui-muted mb-1">Loại</div>
                    <Select
                      value={blkType}
                      style={{ width: 130 }}
                      onChange={(v) => setBlkType(v)}
                      options={[
                        { value: 'IP', label: 'IP' },
                        { value: 'USER', label: 'Tài khoản' },
                      ]}
                    />
                  </div>
                  <div>
                    <div className="text-xs ui-muted mb-1">
                      {blkType === 'IP' ? 'Địa chỉ IP (hoặc 1.2.3.*)' : 'Id người dùng'}
                    </div>
                    <Input
                      style={{ width: 240 }}
                      value={blkValue}
                      onChange={(e) => setBlkValue(e.target.value)}
                      placeholder={blkType === 'IP' ? '203.0.113.5' : 'xxxxxxxx-xxxx-…'}
                    />
                  </div>
                  <div>
                    <div className="text-xs ui-muted mb-1">Lý do</div>
                    <Input
                      style={{ width: 220 }}
                      value={blkReason}
                      onChange={(e) => setBlkReason(e.target.value)}
                      placeholder="VD: dò tấn công liên tiếp"
                    />
                  </div>
                  <div>
                    <div className="text-xs ui-muted mb-1">Hết hạn sau</div>
                    <Select
                      value={blkHours}
                      style={{ width: 150 }}
                      onChange={(v) => setBlkHours(v)}
                      options={[
                        { value: 1, label: '1 giờ' },
                        { value: 24, label: '24 giờ' },
                        { value: 168, label: '7 ngày' },
                        { value: 0, label: 'Vĩnh viễn' },
                      ]}
                    />
                  </div>
                  <Button
                    type="primary"
                    danger
                    onClick={() => doAddBlock(blkType, blkValue, blkReason, blkHours)}
                  >
                    Chặn
                  </Button>
                </div>

                <Table
                  rowKey="id"
                  size="small"
                  loading={loadingBlocks}
                  dataSource={blocks}
                  scroll={{ x: 800 }}
                  pagination={false}
                  columns={
                    [
                      {
                        title: 'Loại',
                        dataIndex: 'blockType',
                        width: 110,
                        render: (v: string) => (
                          <Tag color={v === 'IP' ? 'volcano' : 'geekblue'}>
                            {v === 'IP' ? 'IP' : 'Tài khoản'}
                          </Tag>
                        ),
                      },
                      { title: 'Giá trị', dataIndex: 'value', render: (v: string) => <code>{v}</code> },
                      { title: 'Lý do', dataIndex: 'reason' },
                      {
                        title: 'Hết hạn',
                        dataIndex: 'expiresAt',
                        width: 170,
                        render: (v: string | null) =>
                          v ? (
                            new Date(v) > new Date() ? (
                              fmtTime(v)
                            ) : (
                              <Tag>Đã hết hạn</Tag>
                            )
                          ) : (
                            <Tag color="red">Vĩnh viễn</Tag>
                          ),
                      },
                      { title: 'Người tạo', dataIndex: 'createdBy', width: 110 },
                      {
                        title: '',
                        width: 110,
                        render: (_: unknown, r: AccessBlock) => (
                          <Popconfirm
                            title={`Bỏ chặn ${r.value}?`}
                            okText="Bỏ chặn"
                            cancelText="Huỷ"
                            onConfirm={() => doRemoveBlock(r)}
                          >
                            <Button size="small">Bỏ chặn</Button>
                          </Popconfirm>
                        ),
                      },
                    ] as never
                  }
                />
              </>
            ),
          },
          {
            key: 'rules',
            label: `Bộ luật (${rules.length})`,
            children: (
              <>
                <div className="adm-strip adm-strip--warning mb-3 text-sm">
                  Luật mới nên để <b>Cảnh báo</b> vài ngày, xem nhật ký thấy chuẩn rồi mới chuyển sang{' '}
                  <b>Chặn</b> — tránh chặn nhầm người dùng thật.
                </div>

                <div className="flex gap-2 flex-wrap items-center mb-3">
                  <Button type="primary" onClick={openCreate}>
                    + Thêm luật
                  </Button>
                  <Input
                    placeholder="Thử một câu truy vấn…"
                    style={{ width: 280 }}
                    value={testText}
                    onChange={(e) => setTestText(e.target.value)}
                    onPressEnter={runTest}
                  />
                  <Button onClick={runTest}>Thử</Button>
                  {testResult && <span className="text-sm text-gray-600 dark:text-gray-300">{testResult}</span>}
                </div>

                <Table
                  rowKey="id"
                  size="small"
                  loading={loadingRules}
                  columns={ruleColumns as never}
                  dataSource={rules}
                  scroll={{ x: 900 }}
                  pagination={false}
                />
              </>
            ),
          },
        ]}
      />
      </AdminCard>

      <Modal
        title={editing ? 'Sửa luật' : 'Thêm luật'}
        open={creating}
        onCancel={() => {
          setCreating(false);
          setEditing(null);
        }}
        onOk={submitRule}
        okText="Lưu"
        cancelText="Huỷ"
      >
        <Form form={form} layout="vertical" initialValues={EMPTY_RULE}>
          <Form.Item name="name" label="Tên luật" rules={[{ required: true, message: 'Nhập tên luật' }]}>
            <Input placeholder="VD: Chặn SQL injection" />
          </Form.Item>
          <Form.Item name="ruleType" label="Loại">
            <Select
              options={[
                { value: 'KEYWORD', label: 'Nội dung xấu (ma tuý, cờ bạc…)' },
                { value: 'ATTACK', label: 'Mẫu tấn công (SQLi, XSS…)' },
              ]}
            />
          </Form.Item>
          <Form.Item name="matchType" label="Kiểu so khớp">
            <Select
              options={[
                { value: 'CONTAINS', label: 'Chứa chuỗi (đơn giản)' },
                { value: 'REGEX', label: 'Regex (nâng cao)' },
              ]}
            />
          </Form.Item>
          <Form.Item name="pattern" label="Mẫu cần bắt" rules={[{ required: true, message: 'Nhập mẫu' }]}>
            <Input.TextArea rows={2} placeholder="VD: cá độ   hoặc   (?i)(union\s+select)" />
          </Form.Item>
          <Form.Item name="severity" label="Mức độ">
            <Select
              options={[
                { value: 'LOW', label: 'Thấp' },
                { value: 'MEDIUM', label: 'Trung bình' },
                { value: 'HIGH', label: 'Cao' },
              ]}
            />
          </Form.Item>
          <Form.Item name="action" label="Hành động khi khớp">
            <Select
              options={[
                { value: 'LOG', label: 'Chỉ ghi nhật ký' },
                { value: 'WARN', label: 'Cảnh báo admin (khuyên dùng trước)' },
                { value: 'BLOCK', label: 'Chặn request luôn' },
              ]}
            />
          </Form.Item>
          <Form.Item name="enabled" label="Bật luật" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="note" label="Ghi chú">
            <Input placeholder="Ghi chú nội bộ" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ManagementQueryGuard;
